// React & Hooks
import { useState, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";

// Material UI Components
import { Box, Stack, Button, Fade } from "@mui/material";

// Contexts
import { AuthContext } from "../../Context/AuthContext";

// Custom Hooks
import useGetClassById from "../../Hooks/useGetClassById";

// UI Components
import { PageHeaderTitle } from "../../Utils/Component";

// Page Styles
import "../../Style/pagestyle.scss";

// Feature Components
import Student from "./Student/Student";
import Schedule from "./Schedule/Schedule";
import Homework from "./Homework/Homework";
import Attendance from "./Attendance/Attendance";
import TeachingTime from "./TeachingTime/TeachingTime";
import ExamScore from "./Exam/ExamScore";
import Performance from "./Performance/Performance";
// import Content from "./Content/Content";
import Lesson from "./Content/Lesson";
import PackagePrice from "./PackagePrice/PackagePrice";
import OptionPrice from "./OptionPrice/OptionPrice";

export default function StudyClassDetail() {
  // Translation context
  const { t } = useContext(AuthContext);

  // Read tab from localStorage or set default
  const handleGetItemTabFromLocalStorage = () =>
    window.localStorage.getItem("student_tab");

  const [tab, setTab] = useState(
    handleGetItemTabFromLocalStorage() || "Student",
  );
  // console.log("tab::", tab)

  // Get classId from query parameters
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const classId = params.get("classId");

  // Fetch class data
  const { classData } = useGetClassById({ id: classId });

  // Persist tab selection to localStorage
  useEffect(() => {
    window.localStorage.setItem("student_tab", tab);
  }, [tab]);

  // Mapping tabs to components
  const TabComponents = {
    Student,
    Schedule,
    Homework,
    Attendance,
    TeachingTime,
    ExamScore,
    Performance,
    // Content,
    Lesson,
    PackagePrice,
    OptionPrice,
  };

  const ActiveComponent = TabComponents[tab];

  // Props passed to each tab component
  const tabPropsMap = {
    Student: { classId, defaultTotalHour: classData?.totalHour },
    Homework: { classId },
    Schedule: { classId, classData },
    Attendance: { classId },
    TeachingTime: { classId },
    ExamScore: { classId },
    Performance: { classId },
    // Content: { classId },
    Lesson: { classId },
    PackagePrice: { classId },
    OptionPrice: { classId },
  };

  const activeProps = tabPropsMap[tab] || {};

  // Tab button definitions
  const tabs = [
    { key: "Student", label: t("page_student") },
    { key: "Schedule", label: t("thead_schedule") },
    { key: "Attendance", label: t("thead_attendance") },
    { key: "Homework", label: t("thead_homework") },
    { key: "TeachingTime", label: t("thead_teaching_time") },
    { key: "ExamScore", label: t("thead_exam") },
    { key: "Performance", label: t("thead_performance") },
  ];

  const onlineTabs = [
    { key: "Student", label: t("page_student") },
    { key: "Schedule", label: t("thead_schedule") },
    { key: "Attendance", label: t("thead_attendance") },
    { key: "Homework", label: t("thead_homework") },
    { key: "TeachingTime", label: t("thead_teaching_time") },
    { key: "ExamScore", label: t("thead_exam") },
    { key: "Performance", label: t("thead_performance") },

    // { key: "Content", label: t("page_content") },
    { key: "Lesson", label: t("page_lesson") },
    { key: "PackagePrice", label: t("page_package_price") },
    { key: "OptionPrice", label: t("page_option_price_name") },
  ];

  return (
    <div className="page-container">
      <PageHeaderTitle
        classPage={t("class-page-title")}
        classSlash="slash-title"
        routeTo="/class"
        pageTitle={t("page_class")}
        detailTitle={
          classData?.courseId?.enName
            ? classData?.courseId?.enName + " " + classData?.className
            : ""
        }
      />

      {/* Tab Navigation */}
      <Box
        className="tab-container"
        sx={{ position: "relative", marginTop: 8 }}
      >
        <Box sx={{ position: "absolute", bottom: "-2px", width: "100%" }}>
          <Stack
            direction="row"
            alignItems="flex-end"
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            {classData?.type === "online" && (
              <Stack direction="row" className="tab">
                {onlineTabs.map(({ key, label }) => (
                  <Button
                    key={key}
                    className={
                      tab === key
                        ? t("class-name-tab-item-active")
                        : t("class-name-tab-item")
                    }
                    onClick={() => setTab(key)}
                  >
                    {label}
                  </Button>
                ))}
              </Stack>
            )}
            {classData?.type === "direct" || classData?.type === "private" ? (
              <Stack direction="row" className="tab">
                {tabs.map(({ key, label }) => (
                  <Button
                    key={key}
                    className={
                      tab === key
                        ? t("class-name-tab-item-active")
                        : t("class-name-tab-item")
                    }
                    onClick={() => setTab(key)}
                  >
                    {label}
                  </Button>
                ))}
              </Stack>
            ) : (
              ""
            )}
          </Stack>
        </Box>
      </Box>

      {/* Tab Content with Fade Transition */}
      <Box>
        <Fade in={true} key={tab} timeout={400}>
          <div>{ActiveComponent && <ActiveComponent {...activeProps} />}</div>
        </Fade>
      </Box>
    </div>
  );
}
